/* eslint-disable no-console */
import { push } from 'react-router-redux';
import { delay } from 'redux-saga';
import { destroy } from 'redux-form';
import { takeEvery, takeLatest, put, select, call } from 'redux-saga/effects';
import { setNotification, getTranslated, searchElementMatch } from 'utils';
import * as PackageTypes from '../types/packageTypes';
import { getConfigurationsApi } from '../api/configurationApi';
import * as PackageApi from '../api/packageApi';
import * as PackageActions from '../actions/packageActions';

const getLayout = state => state.layout;
const getPackage = state => state.packages;

// L O A D   P A C K A G E S
function* loadPackagesSaga() {
    const { limit, offset, sort, filters } = yield select(getPackage);

    try {
        const params = {
            offset,
            limit,
            joins: JSON.stringify(['configurations']),
        };

        const sendFilters = {};

        if (filters.name !== '') {
            sendFilters.name = {
                operator: 'typing',
                value: filters.name,
            };
        }

        if (filters.configurations.length > 0) {
            sendFilters.configurations = filters.configurations;
        }

        if (filters.created_by.length > 0) {
            sendFilters.created_by = filters.created_by;
        }

        params.filter = JSON.stringify(sendFilters);

        if (sort !== null) {
            const sortInt = sort.type === 'asc' ? 1 : -1;

            if (sort.key === 'name') {
                params.sort_by = JSON.stringify({ name: sortInt });
            } else if (sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sortInt });
            }
        }

        const result = yield PackageApi.getPackagesApi(params);

        yield put(PackageActions.loadPackagesRes(result.data));
    } catch (e) {
        console.log(e);
        yield put(PackageActions.loadPackagesErr());
    }
}

export function* loadPackagesWatcher() {
    yield takeLatest(PackageTypes.LOAD_PACKAGES, loadPackagesSaga);
}
// L O A D   P A C K A G E S


// L O A D   C O N F I G U R A T I O N S   F O R   P A C K A G E
function* loadConfigurationsForPackageSaga(action) {
    const { locale } = yield select(getLayout);
    const packageConfigurations = yield select(getPackage);
    const { offset, limit, filters, sort } = packageConfigurations[action.prop];

    try {
        const params = {
            offset,
            limit,
            joins: JSON.stringify(['carriers']),
        };

        const sendFilters = {
            all: true,
        };

        if (filters['name.translate'] !== '') {
            sendFilters['name.translate'] = searchElementMatch(filters['name.translate'], locale);
        }

        if (filters['configuration_property.carrier_id'].length > 0) {
            sendFilters['configuration_property.carrier_id'] = filters['configuration_property.carrier_id'];
        }

        if (filters['configuration_property.property_value_id'].length > 0) {
            sendFilters.and = filters['configuration_property.property_value_id'].map(cp => ({
                'configuration_property.property_value_id': cp,
            }));
        }

        if (filters.configuration_type.length > 0) {
            sendFilters.configuration_type = filters.configuration_type;
        }

        params.filter = JSON.stringify(sendFilters);

        if (sort !== null) {
            const sortInt = sort.type === 'asc' ? 1 : -1;

            if (sort.key === 'name') {
                params.sort_by = JSON.stringify({ 'name.original_name': sortInt });
            } else if (sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sortInt });
            }
        }

        const result = yield getConfigurationsApi(params);

        if (result.status === 200) {
            yield put(PackageActions.loadConfigurationsForPackageRes(action.prop, result.data));
            yield delay(200);
        }
    } catch (e) {
        console.log(e);
        yield put(PackageActions.loadConfigurationsForPackageErr(action.prop));
    }
}

export function* loadConfigurationsForPackageWatcher() {
    yield takeEvery(PackageTypes.LOAD_CONFIGURATIONS_FOR_PACKAGE, loadConfigurationsForPackageSaga);
}
// L O A D   C O N F I G U R A T I O N S   F O R   P A C K A G E


// A D D   N E W   P A C K A G E
function* addConfigurationToPackageSaga(params) {
    yield PackageApi.addConfigurationToPackageApi(params);
}

function* deleteConfigurationFromPackageSaga(params) {
    yield PackageApi.deleteConfigurationFromPackageApi(params);
}

function* addNewPackageSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const { name, composedConfigurations, package_id: packageID } = action.data;

        const params = { package_id: packageID, name };
        const result = yield PackageApi.addNewPackageApi(params);

        if (result.status === 202) {
            const bulkParams = {
                bulk: composedConfigurations.map(c => ({
                    configuration_id: c,
                    package_id: packageID,
                })),
            };

            yield call(addConfigurationToPackageSaga, bulkParams);

            yield put(PackageActions.addNewPackageRes());
            yield put(push('/factory/packages'));

            setNotification('success', locale, 'global.success', 'package.notification.addNewPackageSuccess');
        } else {
            const error = yield result.json();

            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(PackageActions.addNewPackageErr(error.message));
        }
    } catch (e) {
        yield put(PackageActions.addNewPackageErr(getTranslated('package.notification.addNewPackageError', locale)));
        setNotification('error', locale, 'global.error', 'package.notification.addNewPackageError');
        console.log(e);
    }
}

export function* addNewPackageWatcher() {
    yield takeLatest(PackageTypes.ADD_NEW_PACKAGE, addNewPackageSaga);
}
// A D D   N E W   P A C K A G E


// A D D   B U L K   P A C K A G E S
function* addBulkPackages(pack) {
    const progressResult = { package_id: pack.package_id };
    const successResult = { ...progressResult, success: true, error: false };
    const errorResult = { ...progressResult, success: false, error: true };

    try {
        const bulkParams = {
            bulk: pack.configurations.map(c => ({
                configuration_id: c.id,
                package_id: pack.package_id,
            })),
        };

        yield call(addConfigurationToPackageSaga, bulkParams);
        yield put(PackageActions.updateProgressOfBulkPackages(successResult));
    } catch (e) {
        console.log(e);
        yield put(PackageActions.updateProgressOfBulkPackages(errorResult));
    }
}

function* addBulkPackagesSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        for (let i = 0; i < action.data.length; i += 1) {
            const pack = action.data[i];

            try {
                const resultPack = yield PackageApi.getPackagesApi({
                    filter: JSON.stringify({ $and: pack.configurations.map(c => ({ configurations: c.id })) }),
                    limit: pack.configurations.length,
                    offset: 0,
                });

                if (resultPack.data.total === 0) {
                    yield PackageApi.addNewPackageApi({
                        name: pack.name,
                        package_id: pack.package_id,
                    });

                    yield call(addBulkPackages, pack);
                } else {
                    yield put(PackageActions.updateProgressOfBulkPackages({
                        package_id: pack.package_id,
                        success: false,
                        error: true,
                    }));
                }
            } catch (e) {
                yield put(PackageActions.updateProgressOfBulkPackages({
                    package_id: pack.package_id,
                    success: false,
                    error: true,
                }));
                console.log(e);
            }
        }

        yield delay(1000);
        yield put(PackageActions.finishAddingBulkPackages());
        yield put(PackageActions.addBulkPackagesRes());
        yield put(push('/factory/packages'));
        yield put(PackageActions.addToBulkCartesians([]));
        yield put(PackageActions.goToAddBulkPackagesStep(0));
        yield put(destroy('BulkingPackagesForm'));

        setNotification('success', locale, 'global.success', 'package.notification.addBulkPackageSuccess');
    } catch (e) {
        setNotification('error', locale, 'global.error', 'package.notification.addBulkPackageError');
        console.log(e);
    }
}

export function* addBulkPackagesWatcher() {
    yield takeLatest(PackageTypes.ADD_BULK_PACKAGES, addBulkPackagesSaga);
}
// A D D   B U L K   P A C K A G E S


// L O A D   E D I T A B L E   P A C K A G E
function* loadEditablePackageSaga(action) {
    try {
        const params = {
            filter: JSON.stringify({ id: action.id }),
        };

        const result = yield PackageApi.getPackagesApi(params);

        if (result.data.hits.length > 0) {
            const pack = result.data.hits[0];

            const paramsConfig = {
                filter: JSON.stringify({ id: pack.configurations }),
                joins: JSON.stringify(['carriers']),
                limit: pack.configurations.length,
            };

            const resultConfig = yield getConfigurationsApi(paramsConfig);

            pack.configurations = resultConfig.data.hits;

            yield put(PackageActions.loadEditablePackageRes(pack));
        } else {
            yield put(push('/404'));
        }
    } catch (e) {
        console.log(e);
        yield put(PackageActions.loadEditablePackageErr());
    }
}

export function* loadEditablePackageWatcher() {
    yield takeLatest(PackageTypes.LOAD_EDITABLE_PACKAGE, loadEditablePackageSaga);
}
// L O A D   E D I T A B L E   P A C K A G E


// E D I T   P A C K A G E
function* editPackageSaga(action) {
    const { locale } = yield select(getLayout);
    const { edit } = yield select(getPackage);

    try {
        const result = yield PackageApi.editPackageApi(action.data);

        if (result.status === 202) {
            const { configurations } = edit;

            if (configurations.deleted.length > 0) {
                const paramsDelete = {
                    bulk: configurations.deleted.map(c => ({
                        package_id: action.data.package_id,
                        configuration_id: c,
                    })),
                };

                yield call(deleteConfigurationFromPackageSaga, paramsDelete);
                yield delay(280);
            }

            if (configurations.new.length > 0) {
                const paramsAdd = {
                    bulk: configurations.new.map(c => ({
                        package_id: action.data.package_id,
                        configuration_id: c,
                    })),
                };

                yield call(addConfigurationToPackageSaga, paramsAdd);
                yield delay(280);
            }

            yield put(PackageActions.editPackageRes());
            yield put(push('/factory/packages'));

            setNotification('success', locale, 'global.success', 'package.notification.editPackageSuccess');
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });

            yield put(PackageActions.editPackageErr(error.message));
        }
    } catch (e) {
        console.log(e);
        yield put(PackageActions.editPackageErr(getTranslated('package.notification.editPackageError', locale)));
        setNotification('error', locale, 'global.error', 'package.notification.editPackageError');
    }
}

export function* editPackageWatcher() {
    yield takeLatest(PackageTypes.EDIT_PACKAGE, editPackageSaga);
}
// E D I T   P A C K A G E


// T O G G L E   P A C K A G E   S T A T U S
function* togglePackageStatusSaga(action) {
    const { locale } = yield select(getLayout);
    const { id, status } = action;

    try {
        const result = yield PackageApi.togglePackageStatusApi({
            package_id: id,
            status: status === 'enable',
        });

        if (result.status === 202) {
            setNotification('success', locale, 'global.success', `package.notification.toggleStatus.${status}.success`);
            yield put(PackageActions.togglePackageStatusRes());

            yield call(loadPackagesSaga, {});
        } else {
            setNotification('error', locale, 'global.error', `package.notification.toggleStatus.${status}.error`);
        }
    } catch (e) {
        console.log(e);
        setNotification('error', locale, 'global.error', `package.notification.toggleStatus.${status}.error`);
        yield put(PackageActions.togglePackageStatusErr());
    }
}

export function* togglePackageStatusWatcher() {
    yield takeLatest(PackageTypes.TOGGLE_PACKAGE_STATUS, togglePackageStatusSaga);
}
// T O G G L E   P A C K A G E   S T A T U S
